/**
 * Helper to read CSS custom properties
 * CSS is the single source of truth for typography values
 * @param {string} varName - CSS variable name (without dashes)
 * @param {number} defaultValue - Fallback value if CSS variable not found
 * @returns {number} The CSS variable value or default
 */
function getCSSVariable(varName, defaultValue) {
  if (typeof window === "undefined") {
    // Server-side rendering fallback
    return defaultValue;
  }

  const computed = getComputedStyle(document.documentElement);
  const value = computed.getPropertyValue(`--${varName}`).trim();
  return value ? parseFloat(value) : defaultValue;
}

/**
 * Measure actual rendered CSS properties from a DOM element
 * This ensures SVG generation matches CSS-rendered preview exactly
 * @param {HTMLElement} element - The element to measure
 * @returns {Object} Computed style measurements
 */
export function measureCSSRenderedElement(element) {
  if (!element || typeof window === "undefined") {
    return null;
  }

  const computed = getComputedStyle(element);

  return {
    // Typography
    fontSize: parseFloat(computed.fontSize),
    lineHeight: parseFloat(computed.lineHeight),
    fontWeight: computed.fontWeight,
    fontFamily: computed.fontFamily,
    letterSpacing: parseFloat(computed.letterSpacing) || 0,
    opacity: parseFloat(computed.opacity) || 1,
    // Layout
    paddingTop: parseFloat(computed.paddingTop) || 0,
    paddingBottom: parseFloat(computed.paddingBottom) || 0,
    paddingLeft: parseFloat(computed.paddingLeft) || 0,
    paddingRight: parseFloat(computed.paddingRight) || 0,
    gap: parseFloat(computed.gap) || 0,
    margin: parseFloat(computed.margin) || 0,
    marginTop: parseFloat(computed.marginTop) || 0,
    marginBottom: parseFloat(computed.marginBottom) || 0,
    // Dimensions
    width: element.offsetWidth,
    height: element.offsetHeight,
  };
}

/**
 * Create a temporary measurement element to test text rendering
 * Used to ensure SVG text wrapping matches CSS preview exactly
 * @param {string} text - Text to measure
 * @param {string} className - CSS class name to apply for styling
 * @returns {number} Measured text width in pixels
 */
export function measureTextWidthWithCSS(text, className) {
  if (typeof document === "undefined") {
    return 0;
  }

  const tempEl = document.createElement("div");
  tempEl.className = className;
  tempEl.style.position = "absolute";
  tempEl.style.visibility = "hidden";
  tempEl.style.whiteSpace = "pre-wrap";
  tempEl.style.wordWrap = "break-word";
  tempEl.textContent = text;

  document.body.appendChild(tempEl);
  const width = tempEl.offsetWidth;
  document.body.removeChild(tempEl);

  return width;
}

/**
 * Shared font family used across thumbnail and form components
 * Includes Open Sans as primary with system fonts as fallback
 */
export const THUMBNAIL_FONT_FAMILY =
  '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif';

/**
 * Shared thumbnail layout configuration
 * TYPOGRAPHY: Read from CSS Custom Properties (ThumbnailBuilder.module.css)
 * LAYOUT/POSITIONING: Defined here (don't change between CSS and JS)
 * This ensures single source of truth for styling while keeping layout config centralized.
 */
export const THUMBNAIL_LAYOUT = {
  // Physical dimensions
  width: 160,
  height: 160,
  padding: 10,
  borderRadius: 15,
  // borderWidth: 1,

  // Computed values
  contentWidth: 140, // 160 - 10 (left) - 10 (right)
  contentHeight: 140, // 160 - 10 (top) - 10 (bottom)

  // Icon badge positioning
  badge: {
    top: 8,
    right: 8,
    width: 30,
    height: 30,
    borderWidth: 0.5,
    code: {
      fontSize: 19,
      lineHeight: 19,
      fontWeight: 400,
      paddingTop: 1,
    },
    year: {
      fontSize: 8,
      lineHeight: 8,
      fontWeight: 300,
      letterSpacing: 0.1,
      opacity: 0.8,
    },
  },

  // Text container
  textContainer: {
    left: 10,
    top: 46,
    width: 140, // right edge at 150 (160 - 10)
    maxHeight: 70,
    gap: 1,
  },

  // Typography - Creator/Primary (values from CSS)
  primary: {
    fontFamily: THUMBNAIL_FONT_FAMILY,
    fontSize: 19,
    lineHeight: 18,
    fontWeight: 400,
    maxWidth: 140,
    maxLines: 2,
    gap: 0,
  },

  // Typography - Title (values from CSS)
  title: {
    fontFamily: THUMBNAIL_FONT_FAMILY,
    fontSize: 10,
    lineHeight: 15,
    fontWeight: 300,
    maxWidth: 140,
    gap: 1,
    opacity: 0.5,
    // letterSpacing: 0.8,
  },

  // Typography - Subtitle/Secondary (values from CSS)
  secondary: {
    fontFamily: THUMBNAIL_FONT_FAMILY,
    fontSize: 7,
    lineHeight: 9,
    fontWeight: 300,
    maxWidth: 140,
    gap: 1,
    opacity: 0.5,
  },

  // Meta section
  meta: {
    left: 10,
    bottom: 10,
    gap: 1,
  },

  // SKUs (values from CSS)
  sku: {
    fontFamily: THUMBNAIL_FONT_FAMILY,
    fontSize: 16,
    lineHeight: 16,
    fontWeight: 400,
    maxWidth: 150, // 160 - 10 (left padding)
  },

  // SKU Version (lighter variant)
  skuVersion: {
    fontSize: 12,
    fontWeight: 300,
  },

  // CR Number (values from CSS)
  cr: {
    margin: 0,
    paddingTop: 0,
    fontFamily: THUMBNAIL_FONT_FAMILY,
    fontSize: 7,
    lineHeight: 8,
    fontWeight: 300,
    opacity: 0.5,
    maxWidth: 150, // 160 - 10 (left padding)
    letterSpacing: 0.4,
  },
};

/**
 * Measure text width using canvas
 * @param {string} text - Text to measure
 * @param {string} fontString - Canvas font string (e.g., "500 18px Open Sans, sans-serif")
 * @returns {number} Width in pixels
 */
export function measureTextWidth(text, fontString) {
  if (!text) return 0;

  try {
    // Create temporary canvas for measurement
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;

    ctx.font = fontString;
    const metrics = ctx.measureText(text);
    return metrics.width;
  } catch (error) {
    console.warn("Canvas measurement failed, using fallback:", error);
    // Fallback: estimate based on character count and font size
    // For proportional fonts like Open Sans: average char width ≈ font size * 0.55-0.65
    // Extract font size from fontString (format: "fontWeight fontSize fontFamily")
    try {
      const fontSizeMatch = fontString.match(/(\d+)px/);
      const fontSize = fontSizeMatch ? parseInt(fontSizeMatch[1]) : 14;
      // Use more accurate estimate: ~0.6 * fontSize per character
      return text.length * Math.max(3, fontSize * 0.6);
    } catch {
      // Ultimate fallback if parsing fails
      return text.length * 8;
    }
  }
}

/**
 * Wrap text to fit within max width
 * Uses canvas measurement for accurate pixel-based wrapping
 * @param {string} text - Text to wrap
 * @param {number} maxWidth - Maximum width in pixels
 * @param {string} fontString - Canvas font string
 * @param {number} maxLines - Maximum number of lines
 * @returns {string[]} Array of wrapped lines
 */
export function wrapText(text, maxWidth, fontString, maxLines = 2) {
  try {
    if (!text) return [""];

    const lines = [];
    const words = text.split(" ");
    let currentLine = "";

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = measureTextWidth(testLine, fontString);

      if (testWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        // Word doesn't fit on current line
        if (currentLine) {
          lines.push(currentLine);
          if (lines.length >= maxLines) {
            return lines;
          }
        }
        currentLine = word;
      }
    }

    if (currentLine && lines.length < maxLines) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [""];
  } catch (error) {
    console.warn("Text wrapping failed, using fallback:", error);
    // Fallback: split by character limit
    const charLimit = Math.max(10, Math.floor(maxWidth / 8));
    const lines = [];
    const words = text.split(" ");
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + " " + word).trim().length <= charLimit) {
        currentLine = currentLine ? `${currentLine} ${word}` : word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return lines.slice(0, maxLines).length > 0
      ? lines.slice(0, maxLines)
      : [""];
  }
}

/**
 * Truncate text to fit within max width with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxWidth - Maximum width in pixels
 * @param {string} fontString - Canvas font string
 * @returns {string} Truncated text with ellipsis if needed
 */
export function truncateText(text, maxWidth, fontString) {
  try {
    if (!text) return "";

    const ellipsis = "…";
    const textWidth = measureTextWidth(text, fontString);

    if (textWidth <= maxWidth) {
      return text;
    }

    // Binary search for the longest text that fits
    let low = 0;
    let high = text.length;
    let result = "";

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const testText = text.substring(0, mid) + ellipsis;
      const testWidth = measureTextWidth(testText, fontString);

      if (testWidth <= maxWidth) {
        result = testText;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return result || ellipsis;
  } catch (error) {
    console.warn("Text truncation failed, using fallback:", error);
    // Fallback: truncate by character count
    const maxChars = Math.max(5, Math.floor(maxWidth / 8));
    return text.length > maxChars ? text.substring(0, maxChars) + "…" : text;
  }
}

/**
 * Create canvas font string from layout config
 * @param {number} fontSize - Font size in px
 * @param {number} fontWeight - Font weight (400, 500, 600, etc.)
 * @param {string} fontFamily - Font family
 * @returns {string} Canvas font string
 */
export function createFontString(
  fontSize,
  fontWeight,
  fontFamily = '"Open Sans", sans-serif',
) {
  return `${fontWeight} ${fontSize}px ${fontFamily}`;
}
